import React, { useState, useEffect } from 'react';
import { Wallet, Clock, AlertCircle, CheckCircle, Shield, Activity, Key, Lock, Loader } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useMultiChain } from '../contexts/MultiChainProvider';
import ChainSelector from './ChainSelector';
import MultiChainWalletButton from './MultiChainWalletButton';
import ClaimButton from './ClaimButton';
import EVMClaimInterface from './EVMClaimInterface';
import '../App.css';
import '../index.css';

const ConnectPage = () => {
  const { selectedChain, getWalletInfo, EVM_CHAINS, selectedEVMChain } = useMultiChain();
  const walletInfo = getWalletInfo();
  const [isChainLoading, setIsChainLoading] = useState(false);

  // Add loading state when chain changes
  useEffect(() => {
    setIsChainLoading(true);
    const timer = setTimeout(() => setIsChainLoading(false), 500);
    return () => clearTimeout(timer);
  }, [selectedChain]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-900 via-pink-900 to-purple-900 text-white relative">
      {/* Chain Loading Overlay */}
      {isChainLoading && (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-gray-800 rounded-xl p-6 flex flex-col items-center space-y-4">
            <Loader className="w-8 h-8 animate-spin text-green-400" />
            <p className="text-white font-medium">Switching to {selectedChain === 'solana' ? 'Solana' : 'EVM'} Network...</p>
          </div>
        </div>
      )}
      
      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-8 h-8 sm:w-16 sm:h-16 rounded-full bg-pink-400 opacity-20 blur-xl"></div>
        <div className="absolute top-40 right-20 w-16 h-16 sm:w-32 sm:h-32 rounded-full bg-pink-400 opacity-20 blur-xl"></div>
        <div className="absolute bottom-40 left-1/4 w-12 h-12 sm:w-24 sm:h-24 rounded-full bg-pink-500 opacity-10 blur-xl"></div>
        <div className="absolute bottom-20 right-1/4 w-10 h-10 sm:w-20 sm:h-20 rounded-full bg-pink-300 opacity-20 blur-xl"></div>
      </div>

      {/* Navigation */}
      <nav className="relative z-[9999] px-4 sm:px-6 py-4 sm:py-5 flex justify-between items-center">
        <Link to="/" className="flex items-center space-x-2">
          <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-pink-400 to-pink-600 rounded-lg flex items-center justify-center shadow-lg">
            <span className="text-white font-bold text-sm sm:text-lg">S</span>
          </div>
          <span className="font-bold text-lg sm:text-xl">TerraCR</span>
        </Link>
        
        <div className="flex items-center space-x-2 sm:space-x-4">
          <ChainSelector />
          <MultiChainWalletButton />
        </div>
      </nav>

      {/* Hero Section */}
      <main className="relative z-1 max-w-6xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 pb-16 sm:pb-24">
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 bg-gradient-to-r from-pink-400 to-pink-600 inline-block text-transparent bg-clip-text">
            Troubleshoot Your Dapps Issues
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto px-4">
            Connect your wallet to access our advanced troubleshooting tools and get your crypto back on track.
          </p>
          
          {/* Chain Status */}
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4">
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${selectedChain === 'solana' ? 'bg-pink-400' : 'bg-purple-400'}`}></div>
              <span className="text-gray-400 text-sm sm:text-base">
                {selectedChain === 'solana' ? 'Solana Network' : `${EVM_CHAINS[selectedEVMChain]?.name || 'EVM Chain'}`}
              </span>
            </div>
            {walletInfo.isConnected && (
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4 text-pink-400" />
                <span className="text-pink-400 text-sm">Wallet Connected</span>
              </div>
            )}
          </div>
          
          {/* Mobile Chain Indicator */}
          <div className="mt-4 sm:hidden">
            <div className={`inline-flex items-center space-x-2 px-3 py-2 rounded-lg ${selectedChain === 'solana' ? 'bg-pink-500/20 border border-pink-500/30' : 'bg-purple-500/20 border border-purple-500/30'}`}>
              <div className={`w-2 h-2 rounded-full ${selectedChain === 'solana' ? 'bg-pink-400' : 'bg-purple-400'}`}></div>
              <span className={`text-xs font-medium ${selectedChain === 'solana' ? 'text-pink-400' : 'text-purple-400'}`}>
                {selectedChain === 'solana' ? 'SOL' : 'EVM'}
              </span>
            </div>
          </div>
        </div>

        {/* Troubleshooting Card */}
        <div className="max-w-md mx-auto rounded-2xl backdrop-blur-lg bg-white/10 shadow-xl overflow-hidden">
          {/* Card Header */}
          <div className={`px-4 sm:px-6 py-4 border-b ${selectedChain === 'solana' ? 'bg-pink-800/50 border-pink-700/50' : 'bg-purple-800/50 border-purple-700/50'}`}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg sm:text-xl font-semibold">
                {selectedChain === 'solana' ? 'Solana Troubleshooting Portal' : 'EVM Troubleshooting Portal'}
              </h2>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${selectedChain === 'solana' ? 'bg-pink-600' : 'bg-purple-600'}`}>
                <span className="text-white text-xs font-bold">
                  {selectedChain === 'solana' ? 'S' : 'E'}
                </span>
              </div>
            </div>
          </div>

          {/* Card Content */}
          <div className="px-4 sm:px-6 py-6">
            {selectedChain === 'solana' ? (
              <ClaimButton />
            ) : (
              <EVMClaimInterface />
            )}
          </div>
        </div>

        {/* How Our Troubleshooting Works */}
        <div className="mt-16 sm:mt-24">
          <h2 className="text-xl sm:text-2xl font-bold text-center mb-8 sm:mb-12">How Our Troubleshooting Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/10 transition-all">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${selectedChain === 'solana' ? 'bg-green-700/50' : 'bg-blue-700/50'}`}>
                <Wallet size={20} className={selectedChain === 'solana' ? 'text-green-400' : 'text-blue-400'} />
              </div>
              <h3 className="text-lg font-semibold mb-2">Connect Wallet</h3>
              <p className="text-gray-400">Link your {selectedChain === 'solana' ? 'Solana' : 'EVM'} wallet to access our diagnostic tools.</p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/10 transition-all">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${selectedChain === 'solana' ? 'bg-green-700/50' : 'bg-blue-700/50'}`}>
                <Activity size={20} className={selectedChain === 'solana' ? 'text-green-400' : 'text-blue-400'} />
              </div>
              <h3 className="text-lg font-semibold mb-2">Diagnose Issues</h3>
              <p className="text-gray-400">Our AI-powered system identifies and analyzes {selectedChain === 'solana' ? 'Solana' : 'EVM'} wallet problems.</p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/10 transition-all">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${selectedChain === 'solana' ? 'bg-green-700/50' : 'bg-blue-700/50'}`}>
                <Shield size={20} className={selectedChain === 'solana' ? 'text-green-400' : 'text-blue-400'} />
              </div>
              <h3 className="text-lg font-semibold mb-2">Secure Recovery</h3>
              <p className="text-gray-400">Get your {selectedChain === 'solana' ? 'Solana' : 'EVM'} wallet back on track with military-grade security.</p>
            </div>
          </div>
        </div>

        {/* Troubleshooting Services */}
        <div className="mt-16 sm:mt-24">
          <h2 className="text-xl sm:text-2xl font-bold text-center mb-8 sm:mb-12">Our Troubleshooting Services</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
            {selectedChain === 'solana' ? (
              // Solana-specific services
              <>
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6 text-center hover:bg-white/10 transition-all">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-700/50 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Key size={18} className="sm:w-5 sm:h-5 text-green-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold mb-2">Lost Keys Recovery</h3>
                  <p className="text-gray-400 text-sm sm:text-base">Recover lost Solana seed phrases and private keys.</p>
                </div>
                
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6 text-center hover:bg-white/10 transition-all">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-700/50 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Lock size={18} className="sm:w-5 sm:h-5 text-green-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold mb-2">Security Issues</h3>
                  <p className="text-gray-400 text-sm sm:text-base">Fix compromised Solana wallets and security breaches.</p>
                </div>
                
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6 text-center hover:bg-white/10 transition-all">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-700/50 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Clock size={18} className="sm:w-5 sm:h-5 text-green-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold mb-2">Sync Problems</h3>
                  <p className="text-gray-400 text-sm sm:text-base">Resolve Solana blockchain synchronization issues.</p>
                </div>
                
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6 text-center hover:bg-white/10 transition-all">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-700/50 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <AlertCircle size={18} className="sm:w-5 sm:h-5 text-green-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold mb-2">Transaction Errors</h3>
                  <p className="text-gray-400 text-sm sm:text-base">Fix failed Solana transactions and stuck funds.</p>
                </div>
              </>
            ) : (
              // EVM-specific services
              <>
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6 text-center hover:bg-white/10 transition-all">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-700/50 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Key size={18} className="sm:w-5 sm:h-5 text-blue-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold mb-2">Private Key Recovery</h3>
                  <p className="text-gray-400 text-sm sm:text-base">Recover lost EVM private keys and seed phrases.</p>
                </div>
                
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6 text-center hover:bg-white/10 transition-all">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-700/50 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Lock size={18} className="sm:w-5 sm:h-5 text-blue-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold mb-2">Smart Contract Issues</h3>
                  <p className="text-gray-400 text-sm sm:text-base">Fix compromised EVM wallets and contract interactions.</p>
                </div>
                
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6 text-center hover:bg-white/10 transition-all">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-700/50 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Clock size={18} className="sm:w-5 sm:h-5 text-blue-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold mb-2">Gas Issues</h3>
                  <p className="text-gray-400 text-sm sm:text-base">Resolve EVM gas estimation and transaction problems.</p>
                </div>
                
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6 text-center hover:bg-white/10 transition-all">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-700/50 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <AlertCircle size={18} className="sm:w-5 sm:h-5 text-blue-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold mb-2">Network Issues</h3>
                  <p className="text-gray-400 text-sm sm:text-base">Fix failed EVM transactions and network connectivity.</p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Why Choose TerraCR */}
        <div className="mt-16 sm:mt-24">
          <h2 className="text-xl sm:text-2xl font-bold text-center mb-8 sm:mb-12">Why Choose TerraCR for Troubleshooting</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6 text-center hover:bg-white/10 transition-all">
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 ${selectedChain === 'solana' ? 'bg-green-700/50' : 'bg-blue-700/50'}`}>
                <CheckCircle size={18} className={`sm:w-5 sm:h-5 ${selectedChain === 'solana' ? 'text-green-400' : 'text-blue-400'}`} />
              </div>
              <h3 className="text-base sm:text-lg font-semibold mb-2">98% Success Rate</h3>
              <p className="text-gray-400 text-sm sm:text-base">Proven track record of successful {selectedChain === 'solana' ? 'Solana' : 'EVM'} wallet recoveries.</p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6 text-center hover:bg-white/10 transition-all">
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 ${selectedChain === 'solana' ? 'bg-green-700/50' : 'bg-blue-700/50'}`}>
                <Shield size={18} className={`sm:w-5 sm:h-5 ${selectedChain === 'solana' ? 'text-green-400' : 'text-blue-400'}`} />
              </div>
              <h3 className="text-base sm:text-lg font-semibold mb-2">Military-Grade Security</h3>
              <p className="text-gray-400 text-sm sm:text-base">Bank-level encryption protects your {selectedChain === 'solana' ? 'Solana' : 'EVM'} wallet data.</p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6 text-center hover:bg-white/10 transition-all">
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 ${selectedChain === 'solana' ? 'bg-green-700/50' : 'bg-blue-700/50'}`}>
                <Clock size={18} className={`sm:w-5 sm:h-5 ${selectedChain === 'solana' ? 'text-green-400' : 'text-blue-400'}`} />
              </div>
              <h3 className="text-base sm:text-lg font-semibold mb-2">24/7 Expert Support</h3>
              <p className="text-gray-400 text-sm sm:text-base">Round-the-clock assistance from {selectedChain === 'solana' ? 'Solana' : 'EVM'} blockchain specialists.</p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-green-800/50 mt-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">S</span>
              </div>
              <span className="font-bold">TerraCR</span>
            </div>
            <div className="text-xs sm:text-sm text-gray-400 text-center md:text-left">
              © 2024 TerraCR. Professional Dapps Troubleshooting Services.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ConnectPage; 